import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { MailCheck } from "lucide-react";
import { toast } from "sonner";

const COOLDOWN_SECONDS = 60;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [secondsLeft]);

  const send = async () => {
    try {
      await api.post("/auth/forgot-password", { email });
      return true;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return false;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await send();
    if (ok) { setSent(true); setSecondsLeft(COOLDOWN_SECONDS); }
    setLoading(false);
  };

  const resend = async () => {
    if (secondsLeft > 0) return;
    setResending(true);
    const ok = await send();
    if (ok) { setSecondsLeft(COOLDOWN_SECONDS); toast.success("Reset link resent"); }
    setResending(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg px-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-10">
          <LogoMark size="text-3xl" />
        </Link>

        <div className="bg-white border border-brand-line rounded-lg p-8 shadow-sm">
          {sent ? (
            <div className="text-center">
              <MailCheck className="h-10 w-10 mx-auto text-brand-gold" strokeWidth={1.4} />
              <h1 className="font-serif text-2xl text-brand-ink mt-4">Check your inbox</h1>
              <p className="text-sm text-brand-inkLight mt-3 leading-relaxed">
                If an account exists for <span className="font-medium text-brand-ink">{email}</span>, we&apos;ve sent a password-reset link. It will expire in 1 hour.
              </p>

              <button
                onClick={resend}
                disabled={secondsLeft > 0 || resending}
                className="mt-6 text-sm text-brand-maroon hover:underline disabled:text-brand-inkMute disabled:no-underline disabled:cursor-not-allowed"
                data-testid="forgot-resend"
              >
                {resending
                  ? "Resending…"
                  : secondsLeft > 0
                    ? `Resend in ${secondsLeft}s`
                    : "Didn't get it? Resend"}
              </button>

              <p className="text-[11px] text-brand-inkMute mt-4">
                Tip for the dev team: in this environment the reset link is also logged to <code className="text-brand-maroon">backend.err.log</code>.
              </p>
              <Link to="/login" className="btn-ghost inline-block mt-6 text-sm py-2" data-testid="forgot-back-to-login">Back to sign-in</Link>
            </div>
          ) : (
            <>
              <p className="overline">Reset password</p>
              <h1 className="font-serif text-3xl text-brand-ink mt-2">Forgot your password?</h1>
              <p className="text-sm text-brand-inkLight mt-2">Enter your email and we&apos;ll send you a secure reset link.</p>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-form">
                <div>
                  <label className="overline">Email</label>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-elegant mt-1" data-testid="forgot-email" />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="forgot-submit">
                  {loading ? "Sending…" : "Send reset link"}
                </button>
                <p className="text-xs text-center text-brand-inkLight">
                  Remembered it? <Link to="/login" className="text-brand-maroon hover:underline">Sign in</Link>
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
